// ════════════════════════════════════════════════════════════════════════════
// M1.5 Madurez — panel de escenarios what-if (prompt 0739, MVP Lewin)
// Crea escenarios de cambio estructural y modela su CAMPO DE FUERZAS (impulsoras
// vs restrictivas). El score de Lewin (lo calcula el backend) dice si el cambio
// es viable. Consume /structure/madurez/escenarios. Design system V·IAnet.
// ════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../../../../i18n';
import {
  listarEscenariosMadurez,
  crearEscenarioMadurez,
  agregarFuerzaMadurez,
  eliminarFuerzaMadurez,
  evaluarEscenarioMadurez,
  type EscenarioEstructura,
  type TipoFuerza,
} from '../../../../shared/services/api';
import { COLORS, SectionCard, Badge } from './analisis-ui';

export function EscenariosWhatIf() {
  const { t } = useI18n();
  const s = useCallback(
    (k: string, fb: string): string => {
      const mk = ((t.structure as Record<string, unknown>)?.madurez ?? {}) as Record<
        string,
        unknown
      >;
      return typeof mk[k] === 'string' ? (mk[k] as string) : fb;
    },
    [t],
  );

  const [escenarios, setEscenarios] = useState<EscenarioEstructura[]>([]);
  const [abierto, setAbierto] = useState<string | null>(null);
  const [nombre, setNombre] = useState('');
  const [ajuste, setAjuste] = useState(1);
  const [tipo, setTipo] = useState<TipoFuerza>('IMPULSORA');
  const [descFuerza, setDescFuerza] = useState('');
  const [peso, setPeso] = useState(3);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(() => {
    listarEscenariosMadurez()
      .then((r) => setEscenarios(r.data))
      .catch((e: Error) => setError(e.message));
  }, []);
  useEffect(() => cargar(), [cargar]);

  const reemplazar = (e: EscenarioEstructura) =>
    setEscenarios((prev) => prev.map((x) => (x.id === e.id ? e : x)));

  const crear = useCallback(async () => {
    if (!nombre.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await crearEscenarioMadurez({
        nombre: nombre.trim(),
        ajusteCultural: ajuste,
      });
      setEscenarios((prev) => [data, ...prev]);
      setNombre('');
      setAbierto(data.id);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [nombre, ajuste]);

  const addFuerza = useCallback(
    async (id: string) => {
      if (!descFuerza.trim()) return;
      setBusy(true);
      setError(null);
      try {
        const { data } = await agregarFuerzaMadurez(id, {
          tipo,
          descripcion: descFuerza.trim(),
          peso,
        });
        reemplazar(data);
        setDescFuerza('');
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [tipo, descFuerza, peso],
  );

  const quitarFuerza = async (id: string, fuerzaId: string) => {
    setBusy(true);
    try {
      const { data } = await eliminarFuerzaMadurez(id, fuerzaId);
      reemplazar(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const evaluar = async (id: string) => {
    setBusy(true);
    try {
      const { data } = await evaluarEscenarioMadurez(id);
      reemplazar(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    fontSize: 12,
    fontFamily: "'DM Sans',sans-serif",
    border: `1px solid ${COLORS.gm}`,
    borderRadius: 6,
    padding: '6px 8px',
  } as const;
  const btn = (bg: string, fg: string) => ({
    fontSize: 12,
    fontWeight: 700,
    padding: '6px 12px',
    borderRadius: 6,
    border: 'none',
    background: bg,
    color: fg,
    cursor: busy ? 'not-allowed' : 'pointer',
    opacity: busy ? 0.6 : 1,
    fontFamily: "'DM Sans',sans-serif",
  });

  return (
    <SectionCard
      title={s('escenarios', 'Escenarios what-if')}
      hint={s('escenariosHint', 'campo de fuerzas de Lewin')}
      accent={COLORS.cyan}
    >
      {/* Crear */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 10,
        }}
      >
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder={s('escenarioNombre', 'Nombre del escenario (p.ej. Reorganización ventas)')}
          style={{ ...inputStyle, flex: 1, minWidth: 220 }}
        />
        <label
          style={{ fontSize: 11, color: COLORS.go, display: 'flex', alignItems: 'center', gap: 4 }}
        >
          {s('ajusteCultural', 'Ajuste cultural')}
          <input
            type="number"
            min={0.5}
            max={1.5}
            step={0.1}
            value={ajuste}
            onChange={(e) => setAjuste(Number(e.target.value))}
            style={{ ...inputStyle, width: 64 }}
          />
        </label>
        <button onClick={crear} disabled={busy} style={btn(COLORS.a1, COLORS.w)}>
          {s('nuevoEscenario', 'Crear escenario')}
        </button>
      </div>

      {error && <div style={{ color: '#7B0000', fontSize: 11, marginBottom: 8 }}>{error}</div>}

      {escenarios.length === 0 ? (
        <div style={{ fontSize: 12, color: COLORS.go, fontStyle: 'italic' }}>
          {s(
            'sinEscenarios',
            'Aún no hay escenarios. Crea uno para simular un cambio estructural.',
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {escenarios.map((esc) => {
            const score = esc.score != null ? Number(esc.score) : null;
            const open = abierto === esc.id;
            return (
              <div key={esc.id} style={{ border: `1px solid ${COLORS.gm}`, borderRadius: 8 }}>
                <div
                  onClick={() => setAbierto(open ? null : esc.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    cursor: 'pointer',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ fontWeight: 700, color: COLORS.a1, fontSize: 13 }}>
                    {open ? '▾' : '▸'} {esc.nombre}
                  </span>
                  <Badge tono={esc.estado === 'EVALUADO' ? 'azul' : 'neutro'}>{esc.estado}</Badge>
                  {score != null && (
                    <>
                      <span style={{ fontSize: 12, color: COLORS.go }}>
                        {s('score', 'Score')}: <strong style={{ color: COLORS.a1 }}>{score}</strong>
                      </span>
                      <Badge tono={esc.viable ? 'verde' : 'rojo'}>
                        {esc.viable ? s('viable', 'Viable') : s('noViable', 'No viable')}
                      </Badge>
                    </>
                  )}
                </div>

                {open && (
                  <div style={{ padding: '0 10px 10px', borderTop: `1px solid ${COLORS.gc}` }}>
                    {/* Campo de fuerzas */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 10,
                        margin: '10px 0',
                      }}
                    >
                      {(['IMPULSORA', 'RESTRICTIVA'] as TipoFuerza[]).map((tf) => {
                        const list = esc.fuerzas.filter((f) => f.tipo === tf);
                        const color = tf === 'IMPULSORA' ? COLORS.verde : COLORS.rojo;
                        return (
                          <div key={tf}>
                            <div style={{ fontSize: 11, fontWeight: 800, color, marginBottom: 4 }}>
                              {tf === 'IMPULSORA'
                                ? `↑ ${s('impulsoras', 'Impulsoras')}`
                                : `↓ ${s('restrictivas', 'Restrictivas')}`}
                            </div>
                            {list.length === 0 ? (
                              <div style={{ fontSize: 11, color: COLORS.go, fontStyle: 'italic' }}>
                                —
                              </div>
                            ) : (
                              list.map((f) => (
                                <div
                                  key={f.id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    fontSize: 12,
                                    color: COLORS.ink,
                                    marginBottom: 3,
                                  }}
                                >
                                  <span style={{ fontWeight: 800, color }}>{f.peso}</span>
                                  <span style={{ flex: 1 }}>{f.descripcion}</span>
                                  <button
                                    onClick={() => quitarFuerza(esc.id, f.id)}
                                    disabled={busy}
                                    title={s('eliminar', 'Eliminar')}
                                    style={{
                                      border: 'none',
                                      background: 'none',
                                      color: COLORS.go,
                                      cursor: 'pointer',
                                      fontSize: 13,
                                    }}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Añadir fuerza */}
                    <div
                      style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}
                    >
                      <div style={{ display: 'flex', gap: 4 }}>
                        {(['IMPULSORA', 'RESTRICTIVA'] as TipoFuerza[]).map((tf) => (
                          <button
                            key={tf}
                            onClick={() => setTipo(tf)}
                            style={{
                              ...btn(
                                tipo === tf
                                  ? tf === 'IMPULSORA'
                                    ? COLORS.verde
                                    : COLORS.rojo
                                  : COLORS.w,
                                tipo === tf ? COLORS.w : COLORS.go,
                              ),
                              border: `1px solid ${COLORS.gm}`,
                              padding: '4px 8px',
                            }}
                          >
                            {tf === 'IMPULSORA' ? '↑' : '↓'}
                          </button>
                        ))}
                      </div>
                      <input
                        value={descFuerza}
                        onChange={(e) => setDescFuerza(e.target.value)}
                        placeholder={s('fuerzaDesc', 'Describe la fuerza…')}
                        style={{ ...inputStyle, flex: 1, minWidth: 160 }}
                      />
                      <input
                        type="number"
                        min={1}
                        max={5}
                        value={peso}
                        onChange={(e) => setPeso(Number(e.target.value))}
                        title={s('peso', 'Peso 1-5')}
                        style={{ ...inputStyle, width: 56 }}
                      />
                      <button
                        onClick={() => addFuerza(esc.id)}
                        disabled={busy}
                        style={btn(COLORS.a2, COLORS.w)}
                      >
                        {s('anadirFuerza', 'Añadir')}
                      </button>
                      {esc.estado !== 'EVALUADO' && (
                        <button
                          onClick={() => evaluar(esc.id)}
                          disabled={busy}
                          style={btn(COLORS.verde, COLORS.w)}
                        >
                          {s('evaluarEscenario', 'Evaluar')}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}
